<template>
  <div>
    <v-btn v-if="!hideTrigger" color="yellow" class="mr-2" @click="openDialog"
      >Add New</v-btn
    >
    <!-- <v-btn color="green" class="" icon x-large @click="openDialog" v-else
      ><v-icon>mdi-plus-box</v-icon></v-btn
    > -->

    <!-- Restock Price Confirmation Dialog -->
    <v-dialog v-model="restockPriceDialog" persistent max-width="550px">
      <v-card>
        <v-card-title class="orange white--text">
          <v-icon left color="white">mdi-cash-sync</v-icon>
          Update Selling Price?
        </v-card-title>
        <v-card-text class="pt-4" v-if="restockItem">
          <v-alert type="info" dense outlined class="mb-4">
            The new unit cost (₱{{
              Number(restockForm.unit_cost).toLocaleString()
            }}) differs from the current unit price (₱{{
              Number(restockItem.unit_price).toLocaleString()
            }}).
          </v-alert>
          <p class="subtitle-2 mb-3">
            Would you like to update the selling price?
          </p>
          <v-radio-group v-model="restockPriceOption" column>
            <v-radio
              value="auto"
              :label="`Accept auto-calculated: ₱${Number(
                restockAutoPrice
              ).toLocaleString()}`"
            ></v-radio>
            <v-radio value="custom" label="Set custom price"></v-radio>
            <v-radio
              value="skip"
              label="Skip — keep current selling price"
            ></v-radio>
          </v-radio-group>
          <v-text-field
            v-if="restockPriceOption === 'custom'"
            type="number"
            label="Custom Selling Price"
            v-model="restockCustomPrice"
            outlined
            dense
            prefix="₱"
            class="mt-2"
          ></v-text-field>
          <div
            class="caption grey--text mt-2"
            v-if="restockPriceOption === 'auto'"
          >
            Calculated: Unit Cost (₱{{
              Number(restockForm.unit_cost).toLocaleString()
            }}) + {{ restockItem.profitable_margin || 0 }}% margin + ₱{{
              Number(restockForm.shipping_cost || 0).toLocaleString()
            }}
            shipping
          </div>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="restockPriceDialog = false">Cancel</v-btn>
          <v-btn
            color="orange"
            dark
            @click="confirmRestockPrice"
            :loading="restockLoading"
          >
            <v-icon left>mdi-check</v-icon>Confirm & Restock
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Restock Dialog -->
    <v-dialog v-model="restockDialog" persistent max-width="550px">
      <v-card>
        <v-card-title class="green white--text">
          <v-icon left color="white">mdi-plus-box</v-icon>
          Add Stock to Existing Item
        </v-card-title>
        <v-card-text class="pt-4" v-if="restockItem">
          <v-alert type="info" dense outlined class="mb-4">
            <strong>{{ restockItem.item_name }}</strong
            ><br />
            Current Stock: <strong>{{ restockItem.quantity }}</strong> | Branch:
            <strong>{{
              restockItem.branch ? restockItem.branch.branch_name : ""
            }}</strong>
          </v-alert>

          <v-text-field
            type="number"
            label="Quantity *"
            v-model="restockForm.quantity"
            outlined
            dense
            prepend-inner-icon="mdi-package-variant-closed-plus"
          ></v-text-field>
          <v-text-field
            type="number"
            label="Unit Cost *"
            v-model="restockForm.unit_cost"
            outlined
            dense
            prepend-inner-icon="mdi-cash"
            prefix="₱"
          ></v-text-field>
          <v-autocomplete
            label="Supplier"
            v-model="restockForm.supplier"
            :items="suppliers"
            item-text="supplier_name"
            item-value="supplier_name"
            outlined
            dense
            prepend-inner-icon="mdi-truck"
          ></v-autocomplete>
          <v-text-field
            label="Purchase Date"
            v-model="restockForm.purchase_date"
            type="date"
            outlined
            dense
            prepend-inner-icon="mdi-calendar"
          ></v-text-field>
          <v-text-field
            type="number"
            label="Shipping Cost"
            v-model="restockForm.shipping_cost"
            outlined
            dense
            prepend-inner-icon="mdi-truck-delivery"
            prefix="₱"
          ></v-text-field>
          <v-radio-group v-model="restockForm.is_vat" row>
            <template v-slot:label>
              <div class="text-body-2 font-weight-bold">Tax Option</div>
            </template>
            <v-radio label="Non-VAT" :value="false"></v-radio>
            <v-radio label="VAT" :value="true"></v-radio>
          </v-radio-group>
          <div class="d-flex align-center">
            <v-text-field
              type="number"
              label="Supplier Warranty"
              v-model="restockForm.supplier_warranty_months"
              outlined
              dense
              prepend-inner-icon="mdi-shield-check"
              class="flex-grow-1 mr-2"
            ></v-text-field>
            <v-select
              v-model="restockForm.supplier_warranty_unit"
              :items="warrantyUnits"
              outlined
              dense
              style="max-width: 140px"
            ></v-select>
          </div>
          <div class="d-flex mb-4">
            <v-chip
              v-for="preset in warrantyPresets[
                restockForm.supplier_warranty_unit || 'months'
              ]"
              :key="preset"
              small
              class="mr-2"
              :color="
                restockForm.supplier_warranty_months == preset
                  ? 'primary'
                  : 'grey lighten-2'
              "
              :dark="restockForm.supplier_warranty_months == preset"
              @click="restockForm.supplier_warranty_months = preset"
            >
              {{ preset
              }}{{
                warrantyUnitShort[
                  restockForm.supplier_warranty_unit || "months"
                ]
              }}
            </v-chip>
          </div>
          <div class="d-flex align-center">
            <v-select
              v-model="restockForm.import_batch_id"
              label="Batch Import"
              :items="import_batches"
              item-value="id"
              item-text="batch_description"
              outlined
              dense
              hide-details
              class="flex-grow-1"
              prepend-inner-icon="mdi-package-variant"
            ></v-select>
            <add-batch-import-modal
              class="ml-2"
              :preset-supplier-id="restockSupplierId"
              @added="onRestockBatchAdded"
            />
          </div>
          <v-text-field
            label="Remarks"
            v-model="restockForm.remarks"
            outlined
            dense
            class="mt-4"
            prepend-inner-icon="mdi-note-text"
          ></v-text-field>
          <v-checkbox
            v-if="!restockItem.is_serialized"
            v-model="restockForm.mark_serialized"
            color="primary"
            dense
            hide-details
            class="mt-0 mb-1"
          >
            <template v-slot:label>
              <span>
                <v-icon small color="primary" class="mr-1">mdi-barcode</v-icon>
                Serialized item — capture a serial per unit
              </span>
            </template>
          </v-checkbox>
          <div
            v-if="!restockItem.is_serialized && restockForm.mark_serialized"
            class="caption orange--text mb-2"
          >
            This marks the item serialized. Existing stock ({{
              restockItem.quantity
            }}) stays un-serialized until you backfill it on the item's Serial
            Numbers tab.
          </div>
          <serial-numbers-input
            v-if="restockItem.is_serialized || restockForm.mark_serialized"
            v-model="restockForm.serials_text"
            :quantity="restockForm.quantity"
          />
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="restockDialog = false">Cancel</v-btn>
          <v-btn
            color="green"
            dark
            @click="submitRestock"
            :loading="restockLoading"
          >
            <v-icon left>mdi-check</v-icon>Add Stock
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialog" persistent max-width="900px" scrollable>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left color="white">mdi-package-variant-plus</v-icon>
          <span class="text-h5">{{
            fromPurchaseOrder ? "Add Item from Purchase Order" : "Add Item"
          }}</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-subtitle v-if="fromPurchaseOrder" class="pt-3 pb-0">
          <v-chip color="blue" outlined small>
            <v-icon left small>mdi-information</v-icon>
            Item details pre-filled from purchase order
          </v-chip>
        </v-card-subtitle>

        <v-divider></v-divider>

        <v-card-text class="pt-4" style="max-height: 600px">
          <!-- Item Search Table (only if not from purchase order) -->
          <v-data-table
            v-if="!fromPurchaseOrder && form.item_name != ''"
            :search="form.item_name"
            :items="branch_filtered_items"
            :headers="itemHeaders"
            :items-per-page="3"
            class="elevation-1 mt-4"
          >
            <template v-slot:top>
              <v-toolbar flat color="grey lighten-4">
                <v-toolbar-title class="text-subtitle-2"
                  >Similar Items Found</v-toolbar-title
                >
              </v-toolbar>
            </template>
            <template v-slot:item.action="{ item }">
              <v-btn
                small
                color="yellow darken-2"
                @click="setItem(item)"
                class="mr-1"
              >
                <v-icon small left>mdi-download</v-icon>Use
              </v-btn>
              <v-btn small color="green" dark @click="openRestockMode(item)">
                <v-icon small left>mdi-plus-box</v-icon>Add Stock
              </v-btn>
            </template>
          </v-data-table>
          <v-form ref="form">
            <!-- Photo Upload Section (only if not from purchase order) -->
            <v-expansion-panels v-if="!fromPurchaseOrder" class="mb-4">
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <span
                    ><v-icon left>mdi-camera</v-icon>Item Photos ({{
                      item_photos.length
                    }}) (Optional)</span
                  >
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <item-photo-manager
                    :photos="item_photos"
                    @photo-added="onPhotoAdded"
                    @photo-removed="onPhotoRemoved"
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- Quick Actions (only if not from purchase order) -->
            <div v-if="!fromPurchaseOrder" class="mb-4 d-flex gap-2">
              <v-btn
                small
                outlined
                color="yellow darken-2"
                @click="set_item_for_add"
              >
                <v-icon left small>mdi-package</v-icon>Get Item
              </v-btn>
              <v-btn small outlined color="grey" @click="get_coa">
                <v-icon left small>mdi-refresh</v-icon>Refresh COAs
              </v-btn>
            </div>

            <!-- Main Form - Two Column Layout -->
            <v-row>
              <!-- Left Column -->
              <v-col cols="12" md="6">
                <v-subheader class="px-0 font-weight-bold"
                  >Basic Information</v-subheader
                >

                <v-text-field
                  label="Item Name *"
                  v-model="form.item_name"
                  outlined
                  dense
                  :readonly="fromPurchaseOrder"
                  prepend-inner-icon="mdi-tag"
                ></v-text-field>

                <v-autocomplete
                  label="Item Type *"
                  v-model="form.item_type_id"
                  :items="item_types"
                  item-text="item_type"
                  item-value="id"
                  outlined
                  dense
                  prepend-inner-icon="mdi-shape"
                ></v-autocomplete>

                <v-autocomplete
                  label="Sub-Type"
                  v-model="form.item_sub_type_id"
                  :items="filtered_sub_types"
                  item-text="item_sub_type"
                  item-value="id"
                  outlined
                  dense
                  clearable
                  :disabled="!form.item_type_id"
                  prepend-inner-icon="mdi-shape-outline"
                  no-data-text="No sub-types for this type"
                ></v-autocomplete>

                <v-select
                  label="Part Classification"
                  v-model="form.part_classification"
                  :items="partClassificationOptions"
                  outlined
                  dense
                  clearable
                  prepend-inner-icon="mdi-tag-outline"
                ></v-select>

                <v-select
                  label="Account Title *"
                  :items="chart_of_accounts"
                  item-text="account_title"
                  item-value="id"
                  v-model="form.chart_of_account_id"
                  outlined
                  dense
                  prepend-inner-icon="mdi-book-open-variant"
                ></v-select>

                <v-text-field
                  label="Unit *"
                  v-model="form.unit"
                  outlined
                  dense
                  :readonly="fromPurchaseOrder"
                  prepend-inner-icon="mdi-package"
                ></v-text-field>

                <v-autocomplete
                  label="Supplier"
                  v-model="form.supplier"
                  :items="suppliers"
                  item-text="supplier_name"
                  item-value="supplier_name"
                  outlined
                  dense
                  prepend-inner-icon="mdi-truck"
                ></v-autocomplete>
              </v-col>

              <!-- Right Column -->
              <v-col cols="12" md="6">
                <v-subheader class="px-0 font-weight-bold"
                  >Pricing & Stock</v-subheader
                >

                <v-text-field
                  type="number"
                  label="Unit Price *"
                  v-model="form.unit_price"
                  outlined
                  dense
                  :readonly="fromPurchaseOrder"
                  prepend-inner-icon="mdi-cash"
                  prefix="₱"
                ></v-text-field>

                <v-text-field
                  type="number"
                  label="Profitable Margin (%)"
                  v-model="form.profitable_margin"
                  @input="calculate_selling_price"
                  outlined
                  dense
                  prepend-inner-icon="mdi-percent"
                  hint="Profit percentage to add to unit price"
                ></v-text-field>

                <v-text-field
                  type="number"
                  label="Shipping Cost"
                  v-model="form.shipping_cost"
                  @input="calculate_selling_price"
                  outlined
                  dense
                  prepend-inner-icon="mdi-truck-delivery"
                  prefix="₱"
                ></v-text-field>

                <v-text-field
                  type="number"
                  :label="
                    `Selling Price *` +
                    (selectedSellingPrice
                      ? ` (Previous: ₱${selectedSellingPrice})`
                      : '')
                  "
                  v-model="form.selling_price"
                  outlined
                  dense
                  prepend-inner-icon="mdi-cash-multiple"
                  prefix="₱"
                ></v-text-field>

                <v-text-field
                  type="number"
                  label="Initial Import (Quantity) *"
                  v-model="form.initial_import"
                  outlined
                  dense
                  prepend-inner-icon="mdi-package-variant-closed-plus"
                ></v-text-field>

                <v-checkbox
                  v-if="!item_transfer"
                  v-model="form.is_serialized"
                  label="Serialized item (each unit has a serial number)"
                  dense
                  class="mt-0"
                ></v-checkbox>

                <serial-numbers-input
                  v-if="form.is_serialized && !item_transfer"
                  v-model="form.serials_text"
                  :quantity="form.initial_import"
                />

                <v-text-field
                  type="number"
                  label="Reorder Point"
                  v-model="form.reorder_point"
                  outlined
                  dense
                  prepend-inner-icon="mdi-alert-circle-outline"
                  hint="Alert when stock falls below this level"
                ></v-text-field>

                <v-text-field
                  type="number"
                  label="Min Stock Level"
                  v-model="form.min_stock_level"
                  outlined
                  dense
                  prepend-inner-icon="mdi-package-variant-closed-minus"
                  hint="Minimum stock to maintain"
                ></v-text-field>

                <v-radio-group v-model="form.is_vat" row>
                  <template v-slot:label>
                    <div class="text-body-2 font-weight-bold">Tax Option</div>
                  </template>
                  <v-radio label="Non-VAT" :value="0"></v-radio>
                  <v-radio label="VAT" :value="1"></v-radio>
                </v-radio-group>

                <div class="d-flex align-center">
                  <v-text-field
                    type="number"
                    label="Supplier Warranty"
                    v-model="form.supplier_warranty_months"
                    outlined
                    dense
                    prepend-inner-icon="mdi-shield-check"
                    class="flex-grow-1 mr-2"
                  ></v-text-field>
                  <v-select
                    v-model="form.supplier_warranty_unit"
                    :items="warrantyUnits"
                    outlined
                    dense
                    style="max-width: 140px"
                  ></v-select>
                </div>
                <div class="d-flex mb-4">
                  <v-chip
                    v-for="preset in warrantyPresets[
                      form.supplier_warranty_unit || 'months'
                    ]"
                    :key="preset"
                    small
                    class="mr-2"
                    :color="
                      form.supplier_warranty_months == preset
                        ? 'primary'
                        : 'grey lighten-2'
                    "
                    :dark="form.supplier_warranty_months == preset"
                    @click="form.supplier_warranty_months = preset"
                  >
                    {{ preset
                    }}{{
                      warrantyUnitShort[form.supplier_warranty_unit || "months"]
                    }}
                  </v-chip>
                </div>
              </v-col>
            </v-row>

            <!-- Warranty & Batch Section -->
            <v-divider class="my-4"></v-divider>
            <v-subheader class="px-0 font-weight-bold"
              >Warranty & Batch</v-subheader
            >

            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  label="Warranty Count"
                  type="number"
                  v-model="form.warranty_count"
                  outlined
                  dense
                  prepend-inner-icon="mdi-shield-check"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="form.warranty_duration"
                  label="Warranty Duration"
                  :items="['Day/s', 'Week/s', 'Months/s', 'Year']"
                  outlined
                  dense
                ></v-select>
              </v-col>
            </v-row>

            <div class="d-flex align-center">
              <v-select
                v-model="form.import_batch_id"
                label="Batch Import Description *"
                :items="import_batches"
                item-value="id"
                item-text="batch_description"
                outlined
                dense
                prepend-inner-icon="mdi-package-variant"
                class="flex-grow-1"
              ></v-select>
              <add-batch-import-modal
                class="ml-2"
                :preset-supplier-id="addBatchSupplierId"
                @added="onAddFormBatchAdded"
              />
            </div>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="closeDialog"> Cancel </v-btn>
          <v-btn
            color="success"
            @click="submit_item"
            :loading="loading"
            elevation="2"
          >
            <v-icon left>mdi-check</v-icon>
            Add Item
          </v-btn>
        </v-card-actions>
      </v-card></v-dialog
    >
  </div>
</template>
<script>
import { mapGetters, mapActions } from "vuex";
import addBatchImportModal from "../BatchImport/addBatchImportModal.vue";
import ItemPhotoManager from "./ItemPhotoManager.vue";
import serialNumbersInput from "./serialNumbersInput.vue";
import { parseSerials, serialError } from "@/utils/serialNumbers";
import axios from "axios";
export default {
  props: {
    item_transfer: Boolean,
    quantity: [Number, String],
    item: Object,
    fromPurchaseOrder: {
      type: Boolean,
      default: false,
    },
    prefilledData: {
      type: Object,
      default: null,
    },
    // Hide the inline "Add New" trigger button (used on mobile where a FAB
    // opens the dialog via ref instead). Default keeps every other usage intact.
    hideTrigger: {
      type: Boolean,
      default: false,
    },
  },
  components: { addBatchImportModal, ItemPhotoManager, serialNumbersInput },
  data() {
    return {
      show_other: false,
      item_photos: [],
      loading: false,
      ready: false,
      dialog: false,
      selectedSellingPrice: "",
      partClassificationOptions: [
        { text: "Unclassified", value: null },
        { text: "Major Part", value: "major" },
        { text: "Minor Part", value: "minor" },
      ],
      form: {
        item_name: "",
        item_type_id: "",
        item_sub_type_id: "",
        part_classification: null,
        unit_price: "",
        selling_price: "",
        date_received: "",
        supplier: "",
        warranty_count: "",
        initial_import: "",
        warranty_duration: "",
        profitable_margin: "",
        shipping_cost: "",
        vat: 0,
        is_vat: 0,
        unit: "PCS",
        import_batch_id: "",
        chart_of_account_id: 1,
        branch_id: "",
        reorder_point: 0,
        min_stock_level: 0,
        supplier_warranty_months: "",
        supplier_warranty_unit: "months",
        is_serialized: false,
        serials_text: "",
      },
      restockDialog: false,
      restockLoading: false,
      restockItem: null,
      restockForm: {
        quantity: "",
        unit_cost: "",
        supplier: "",
        purchase_date: new Date().toISOString().substr(0, 10),
        shipping_cost: 0,
        is_vat: false,
        supplier_warranty_months: "",
        supplier_warranty_unit: "months",
        import_batch_id: "",
        remarks: "",
        mark_serialized: false,
        serials_text: "",
      },
      warrantyUnits: [
        { text: "Days", value: "days" },
        { text: "Weeks", value: "weeks" },
        { text: "Months", value: "months" },
        { text: "Years", value: "years" },
      ],
      warrantyPresets: {
        days: [30, 60, 90, 180],
        weeks: [4, 12, 26, 52],
        months: [3, 6, 12, 24],
        years: [1, 2, 3, 5],
      },
      warrantyUnitShort: {
        days: "d",
        weeks: "w",
        months: "mo",
        years: "yr",
      },
      // Restock price confirmation
      restockPriceDialog: false,
      restockPriceOption: "auto",
      restockCustomPrice: "",
      restockAutoPrice: 0,
      itemHeaders: [
        { text: "Action", value: "action", sortable: false, width: "220px" },
        { text: "Item Name", value: "item_name" },
        { text: "Qty", value: "quantity" },
        { text: "Batch", value: "import_batch.batch_description" },
        { text: "Branch", value: "branch.branch_name" },
        { text: "Selling Price", value: "selling_price" },
      ],
    };
  },
  methods: {
    openDialog() {
      this.dialog = true;
      // If prefilled data is provided, populate the form
      if (this.prefilledData) {
        this.$nextTick(() => {
          // Use Object.assign to properly merge the data
          Object.keys(this.prefilledData).forEach((key) => {
            if (
              this.prefilledData[key] !== null &&
              this.prefilledData[key] !== undefined
            ) {
              this.form[key] = this.prefilledData[key];
            }
          });
        });
      }
    },

    closeDialog() {
      this.dialog = false;
      this.$emit("closed");
      // Reset form if from purchase order
      if (this.fromPurchaseOrder) {
        this.resetForm();
      }
    },

    resetForm() {
      this.form = {
        item_name: "",
        item_type_id: "",
        item_sub_type_id: "",
        part_classification: null,
        unit_price: "",
        selling_price: "",
        date_received: "",
        supplier: "",
        warranty_count: "",
        initial_import: "",
        warranty_duration: "",
        profitable_margin: "",
        shipping_cost: "",
        vat: 0,
        is_vat: 0,
        unit: "PCS",
        import_batch_id: "",
        chart_of_account_id: 1,
        branch_id: "",
        reorder_point: 0,
        min_stock_level: 0,
        supplier_warranty_months: "",
        supplier_warranty_unit: "months",
        is_serialized: false,
        serials_text: "",
      };
      this.item_photos = [];
      this.selectedSellingPrice = "";
    },

    setItem(item) {
      this.form.item_name = item.item_name;
      this.form.item_type_id = item.item_type_id || "";
      this.form.item_sub_type_id = item.item_sub_type_id || "";
      this.form.shipping_cost = item.shipping_cost;
      this.selectedSellingPrice = item.selling_price;
      this.form.profitable_margin = item.profitable_margin;
      this.form.warranty_count = item.warranty.warranty_count;
      this.form.warranty_duration = item.warranty.warranty_duration;
      this.form.supplier = item.supplier;
    },
    onPhotoAdded(photoData) {
      this.item_photos.push(photoData);
    },
    onPhotoRemoved({ index }) {
      this.item_photos.splice(index, 1);
    },
    set_item_for_add() {
      this.form.item_name = this.item_order_for_add.item_name;
      this.form.item_type_id = this.item_order_for_add.item_type_id || "";
      this.form.item_sub_type_id =
        this.item_order_for_add.item_sub_type_id || "";
      this.form.unit_price = this.item_order_for_add.unit_price;
      this.form.selling_price = this.item_order_for_add.selling_price;
      this.form.shipping_cost = this.item_order_for_add.shipping_cost;
      this.form.profitable_margin = this.item_order_for_add.profitable_margin;
      this.form.import_batch_id = this.item_order_for_add.import_batch.id;
      this.form.supplier = this.item_order_for_add.supplier || "";
      this.form.warranty_count =
        this.item_order_for_add.warranty.warranty_count;
      this.form.warranty_duration =
        this.item_order_for_add.warranty.warranty_duration;
      this.form.initial_import = this.quantity;
      this.form.chart_of_account_id =
        this.item_order_for_add.chart_of_account.id;
      this.item_photos = this.item_order_for_add.item_photos;
      this.form.is_vat = this.item_order_for_add.is_vat;
    },
    async upload_image_to_db(request) {
      await axios
        .post("/item_photo", request)
        .then(() => {
          console.log("Image Successfully uploaded");
        })
        .catch((err) => {
          console.log("uploading image error:" + err);
        });
    },
    get_coa() {
      this.loading = true;
      this.get_chart_of_accounts().then(() => {
        this.loading = false;
      });
    },

    // Auto-select a batch created via the green + in the restock dialog. (JMB-18)
    onRestockBatchAdded(batch) {
      if (batch && batch.id) {
        this.restockForm.import_batch_id = batch.id;
      }
    },
    // Auto-select a batch created via the green + in the add-item form. (JMB-18)
    onAddFormBatchAdded(batch) {
      if (batch && batch.id) {
        this.form.import_batch_id = batch.id;
      }
    },
    openRestockMode(item) {
      this.restockItem = item;
      this.restockForm = {
        quantity: "",
        unit_cost: item.unit_price || "",
        supplier: item.supplier || "",
        purchase_date: new Date().toISOString().substr(0, 10),
        shipping_cost: 0,
        is_vat: item.is_vat ? true : false,
        supplier_warranty_months: "",
        supplier_warranty_unit: "months",
        import_batch_id: item.import_batch_id || "",
        remarks: "",
        mark_serialized: false,
        serials_text: "",
      };
      this.restockDialog = true;
    },
    async submitRestock() {
      if (!this.restockForm.quantity || this.restockForm.quantity <= 0) {
        this.$alert({
          title: "Validation",
          message: "Please enter a quantity greater than 0",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      if (!this.restockForm.unit_cost) {
        this.$alert({
          title: "Validation",
          message: "Please enter a unit cost",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      if (this.restockItem.is_serialized || this.restockForm.mark_serialized) {
        const err = serialError(
          parseSerials(this.restockForm.serials_text),
          this.restockForm.quantity
        );
        if (err) {
          this.$alert({
            title: "Serial Numbers",
            message: err,
            color: "warning",
            icon: "mdi-barcode",
          });
          return;
        }
      }
      // Notify whenever the new unit cost differs from the item's current
      // unit cost so the user can decide whether to adjust the selling price.
      // (JMB-19: previously gated behind margin > 0, so items without a
      // configured profitable margin never surfaced this prompt.)
      const newCost = parseFloat(this.restockForm.unit_cost);
      const oldCost = parseFloat(this.restockItem.unit_price);
      const margin = parseFloat(this.restockItem.profitable_margin) || 0;

      if (newCost !== oldCost) {
        const shipping = parseFloat(this.restockForm.shipping_cost) || 0;
        this.restockAutoPrice = Math.round(
          newCost + (newCost * margin) / 100 + shipping
        );
        // Without a margin the auto price is just cost + shipping, so default
        // to a custom selling price and let the user set it explicitly.
        this.restockPriceOption = margin > 0 ? "auto" : "custom";
        this.restockCustomPrice = "";
        this.restockDialog = false;
        this.restockPriceDialog = true;
      } else {
        await this.doRestock(false);
      }
    },
    async confirmRestockPrice() {
      if (this.restockPriceOption === "skip") {
        await this.doRestock(false);
      } else if (this.restockPriceOption === "auto") {
        await this.doRestock(
          true,
          this.restockForm.unit_cost,
          this.restockAutoPrice
        );
      } else if (this.restockPriceOption === "custom") {
        if (!this.restockCustomPrice || this.restockCustomPrice <= 0) {
          this.$alert({
            title: "Validation",
            message: "Please enter a valid custom selling price",
            color: "warning",
            icon: "mdi-alert",
          });
          return;
        }
        await this.doRestock(
          true,
          this.restockForm.unit_cost,
          this.restockCustomPrice
        );
      }
    },
    async doRestock(updatePrice, newUnitPrice, newSellingPrice) {
      this.restockLoading = true;
      try {
        const payload = { ...this.restockForm };
        delete payload.serials_text;
        if (
          this.restockItem.is_serialized ||
          this.restockForm.mark_serialized
        ) {
          payload.serials = parseSerials(this.restockForm.serials_text);
        }
        if (updatePrice) {
          payload.update_selling_price = true;
          payload.new_unit_price = newUnitPrice;
          payload.new_selling_price = newSellingPrice;
        }
        await this.restock_item({
          itemId: this.restockItem.id,
          payload: payload,
        });
        this.restockLoading = false;
        this.restockDialog = false;
        this.restockPriceDialog = false;
        this.$alert({
          title: "Success",
          message:
            "Stock added successfully!" +
            (updatePrice ? " Selling price updated." : ""),
          color: "success",
          icon: "mdi-check-circle",
        });
        await this.get_items();
      } catch (err) {
        this.restockLoading = false;
        this.$alert({
          title: "Error",
          message:
            "Error: " + (err.response ? err.response.data.error : err.message),
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
    },

    ...mapActions({
      get_items: "item/get_items",
      get_all_items: "item/get_all_items",
      get_import_batches: "importBatch/get_import_batches",
      add_item: "item/add_item",
      restock_item: "item/restock_item",
      get_chart_of_accounts: "chart_of_account/get_chart_of_accounts",
      add_item_transfer: "item_transfer/add_item_transfer",
      get_item_types: "item_type/get_item_types",
      get_item_sub_types: "item_sub_type/get_item_sub_types",
      get_suppliers: "supplier/get_suppliers",
    }),

    calculate_selling_price() {
      // unit_price + percentage + shipping cost
      let sell = (this.form.unit_price * this.form.profitable_margin) / 100;
      let partial = Math.round(parseInt(this.form.unit_price) + sell);
      this.form.selling_price = partial + parseInt(this.form.shipping_cost);
    },
    async submit_item() {
      this.loading = true;
      //check if forms are filled
      if (
        this.form.item_name == "" ||
        this.form.item_type_id == "" ||
        this.form.unit_price == "" ||
        this.form.selling_price == "" ||
        this.form.initial_import == "" ||
        this.form.import_batch_id == ""
      ) {
        this.loading = false;
        this.$alert({
          title: "Validation",
          message: "Please fill up all required fields (*)",
          color: "warning",
          icon: "mdi-alert",
        });
        return;
      }
      if (this.form.is_serialized && !this.item_transfer) {
        const err = serialError(
          parseSerials(this.form.serials_text),
          this.form.initial_import
        );
        if (err) {
          this.loading = false;
          this.$alert({
            title: "Serial Numbers",
            message: err,
            color: "warning",
            icon: "mdi-barcode",
          });
          return;
        }
      }
      try {
        // For transfers, set initial_import to 0 — the transfer controller will create
        // the proper stock lots from the source item's FIFO deduction
        const formPayload = { ...this.form };
        delete formPayload.serials_text;
        if (this.item_transfer) {
          formPayload.initial_import = 0;
          formPayload.is_serialized = false;
        } else if (this.form.is_serialized) {
          formPayload.serials = parseSerials(this.form.serials_text);
        }
        const res = await this.add_item(formPayload);
        // insert images from item_photos array to db
        this.item_photos.forEach((p) => {
          let request = {
            item_id: res.id,
            item_photo_url: p.item_photo_url,
            photo_name: p.photo_name,
          };
          this.upload_image_to_db(request);
        });

        // If this is an item transfer, create the transfer record with the new item as destination
        if (this.item_transfer) {
          const transferPayload = {
            ...this.item,
            to_item_id: res.id,
          };
          await this.add_item_transfer(transferPayload);
          // Refresh items list to reflect updated quantities
          await this.get_items();
        }

        this.loading = false;
        this.$alert({
          title: "Success",
          message: this.item_transfer
            ? "Successfully processed item transfer"
            : "Successfully added a new item",
          color: "success",
          icon: "mdi-check-circle",
        });

        // Emit success event
        this.$emit("item-added", res);

        // Close dialog and reset form
        this.closeDialog();
      } catch (err) {
        this.$alert({
          title: "Error",
          message:
            "Error: " + (err.response?.data?.error || err.message || err),
          color: "error",
          icon: "mdi-alert-circle",
        });
        this.loading = false;
      }
    },
  },
  computed: {
    ...mapGetters({
      all_items: "item/all_items",
      import_batches: "importBatch/import_batches",
      chart_of_accounts: "chart_of_account/chart_of_accounts",
      item_order_for_add: "item/item_order_for_add",
      user: "auth/user",
      item_types: "item_type/item_types",
      item_sub_types: "item_sub_type/item_sub_types",
      suppliers: "supplier/suppliers",
    }),
    // Resolve the supplier chosen in the add-item form (stored as a name) to its
    // id, so the Add Batch Import dialog can pre-select it and auto-fill the
    // batch tag. (JMB-18)
    addBatchSupplierId() {
      if (!this.form.supplier || !this.suppliers) return null;
      const match = this.suppliers.find(
        (s) => s.supplier_name === this.form.supplier
      );
      return match ? match.id : null;
    },
    // Same, but for the restock dialog's supplier. (JMB-18)
    restockSupplierId() {
      if (!this.restockForm.supplier || !this.suppliers) return null;
      const match = this.suppliers.find(
        (s) => s.supplier_name === this.restockForm.supplier
      );
      return match ? match.id : null;
    },
    branch_filtered_items() {
      // When transferring, the item is being added to the DESTINATION branch,
      // so "Similar Items Found" must search that branch's inventory rather
      // than the current user's branch. Otherwise items that already exist in
      // the destination branch can't be found for restock/merge. (JMB-16)
      const targetBranchId =
        this.item_transfer && this.item
          ? this.item.to_branch_id
          : this.user && this.user.branch_id;
      if (!targetBranchId) return this.all_items;
      return this.all_items.filter((item) => item.branch_id === targetBranchId);
    },
    filtered_sub_types() {
      if (!this.form.item_type_id) return [];
      return this.item_sub_types.filter(
        (st) => st.item_type_id == this.form.item_type_id
      );
    },
  },
  watch: {
    "form.item_type_id"(newVal, oldVal) {
      if (oldVal && newVal !== oldVal) {
        this.form.item_sub_type_id = "";
      }
    },
  },
  async created() {
    await this.get_items();
    // Load the cross-branch item list backing "Similar Items Found" so the
    // modal works standalone (e.g. item transfer) without relying on a parent
    // to have fetched it. (JMB-16)
    if (!this.all_items || this.all_items.length === 0) {
      await this.get_all_items();
    }
    await this.get_import_batches();
    await this.get_chart_of_accounts();
    await this.get_item_types();
    await this.get_item_sub_types();
    await this.get_suppliers();
    this.ready = true;

    if (this.item_transfer) {
      this.form.branch_id = this.item.to_branch_id;
      this.form.modal_from = "item transfer";
    } else {
      // insert branch id to form
      this.form.branch_id = this.user.branch_id;
    }
  },
};
</script>

<style lang="scss" scoped></style>
